import { DestroyRef, Service, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { datadogRum } from '@datadog/browser-rum';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AppRoute } from '@app/shared/app.route.enum';
import { Logger } from './logger.service';

/**
 * Sections recognised by their FIRST URL segment. Everything else -- login,
 * users, groups, settings, profile, the dashboard -- stays unrecorded, so a
 * session that never enters a recorded section costs no replay.
 */
export const REPLAY_RECORDED_SECTIONS: readonly string[] = [
  AppRoute.VideoEditor,
  AppRoute.Analytics,
  AppRoute.Podcasts,
];

/**
 * The audio editor cannot be recognised by its first segment. EditorModule is
 * lazy-loaded twice: at the top-level '/editor' route, and -- this is where all
 * real traffic is -- nested under /studios/:meetingId/recordings/:recordingId/editor
 * (meetings.routing.module.ts). PROD RUM over 7 days: 19 sessions on the nested
 * path, 0 on the top-level one. Matching only the first segment would therefore
 * cover the dead route and silently miss every actual audio-editing session.
 */
export const AUDIO_EDITOR_SEGMENT: string = AppRoute.Editor;

/** Splits a router URL into its path segments, dropping query and fragment. */
export function segmentsOf(url: string): string[] {
  return (url.split(/[?#]/, 1)[0] ?? '').split('/').filter(Boolean);
}

/** The first path segment, used for logging and first-segment matching. */
export function sectionOf(url: string): string {
  return segmentsOf(url)[0] ?? '';
}

export function isRecordedSection(url: string): boolean {
  const segments = segmentsOf(url);
  if (REPLAY_RECORDED_SECTIONS.includes(segments[0] ?? '')) {
    return true;
  }
  // Segment equality, never a substring: '/video-editor/1' has the single
  // segment 'video-editor', so it can never match the audio editor by accident,
  // and '/settings/analytics' is not analytics.
  return segments.includes(AUDIO_EDITOR_SEGMENT);
}

/**
 * Drives Datadog Session Replay per section. RUM itself samples every session
 * (sessionSampleRate: 100), but the replay recording is started manually --
 * see initDatadogMonitoring -- and this service is what turns it on while the
 * user is inside a section listed in REPLAY_RECORDED_SECTIONS and off again on
 * the way out. Recording may be stopped and restarted any number of times
 * within one session; the resulting replay simply has gaps where nothing was
 * recorded.
 */
@Service()
export class SessionReplayService {
  private readonly log = new Logger('SessionReplay');
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private recording = false;
  private subscription?: Subscription;

  /**
   * Begins watching navigation. Safe to call more than once -- only the first
   * call subscribes. Evaluates the current URL synchronously first so a deep
   * link straight into a recorded section is captured from the start.
   */
  start(): void {
    if (this.subscription) {
      return;
    }

    this.apply(this.router.url);

    // takeUntilDestroyed already tears this down with the root injector. Keeping
    // the handle also makes repeated start calls idempotent.
    this.subscription = this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => this.apply(event.urlAfterRedirects));
  }

  private apply(url: string): void {
    const shouldRecord = isRecordedSection(url);
    if (shouldRecord === this.recording) {
      return;
    }

    this.recording = shouldRecord;
    if (shouldRecord) {
      datadogRum.startSessionReplayRecording();
      this.log.debug(`Session replay recording started for section '${sectionOf(url)}'`);
    } else {
      datadogRum.stopSessionReplayRecording();
      this.log.debug('Session replay recording stopped');
    }
  }
}
